import { isNotDefined, makeValuesPredicate } from '@sgme/fp';
import classnames from 'classnames';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import { type CSSProperties, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { sortByArray } from 'utils/array/array';
import type { IWorkflowError } from 'utils/errors/workflowErrors';

export interface WorkflowErrorDetailsItemProps {
  useLegacyWorkFlowError?: boolean;
  workflowError: NextWorkflowError;
}

const errorPropertyOrder = [
  'rfqid',
  'rejectiontype',
  'rejectioncode',
  'rejectionmessage',
  'rejectionmessagecode',
  'rejectionorigin',
  'originmessage',
  'outofrangecondition',
  'outofrangehighlimit',
  'outofrangelowlimit',
] as const;
type ErrorProperty = (typeof errorPropertyOrder)[number];

const id = 'errors-panel-details-list-error-message';

const isMessageProperty = makeValuesPredicate(['originmessage', 'rejectionmessage'] satisfies ErrorProperty[]);

// See https://stackoverflow.com/a/13850750
// Otherwise, some error messages containing the long types force the message to overflow.
const forceLineBreaks: CSSProperties = {
  wordBreak: 'break-word',
};

export function WorkflowErrorDetailsItem({
  useLegacyWorkFlowError = false,
  workflowError,
}: WorkflowErrorDetailsItemProps) {
  const { formatMessage, formatNumber } = useIntl();

  const modifiedWorkflowError = useMemo(
    () => (useLegacyWorkFlowError ? mapToLegacyWorkflowError(workflowError) : workflowError),
    [workflowError, useLegacyWorkFlowError],
  );

  const getFormattedValue = useCallback(
    (key: keyof (IWorkflowError | NextWorkflowError)) => {
      const value = modifiedWorkflowError[key] as any;

      if (isNotDefined(value)) {
        return '-';
      }
      if (typeof value === 'string') {
        return value.replace(/:/gi, ': ');
      }

      if (typeof value === 'number') {
        return formatNumber(value);
      }

      return value;
    },
    [modifiedWorkflowError, formatNumber],
  );

  const errorKeys = useMemo(
    () => sortByArray(Object.keys(modifiedWorkflowError), errorPropertyOrder),
    [modifiedWorkflowError],
  );

  return (
    <div className={`${id}-component list-group list-group-flush`} data-e2e={id} id={id}>
      {errorKeys.map(key => {
        const isMessage = isMessageProperty(key.toLowerCase());
        const value = getFormattedValue(key as keyof (IWorkflowError | NextWorkflowError));

        if (!value) {
          return null;
        }

        return (
          <li
            className="list-group-item border-bottom d-md-flex justify-content-md-between flex-wrap px-0 py-2"
            key={key}
          >
            <div
              className={classnames('text-secondary', {
                'w-100 mb-1': isMessage,
              })}
              data-e2e={`error-details-${key.toLowerCase()}-key`}
            >
              {formatMessage({ id: `errorDetails.${key}` as any, defaultMessage: key })}
            </div>
            <div
              className={isMessage ? 'w-100 text-danger' : 'text-truncate'}
              style={forceLineBreaks}
              data-e2e={`error-details-${key.toLowerCase()}-value`}
            >
              {value}
            </div>
          </li>
        );
      })}
    </div>
  );
}

/**
 * @deprecated is this still valid?
 */
function mapToLegacyWorkflowError(error: NextWorkflowError): IWorkflowError {
  return {
    RfqId: error?.rfqId,
    RejectionType: error?.rejectionType,
    RejectionCode: error?.rejectionCode,
    RejectionOrigin: error?.rejectionOrigin,
    RejectionMessage: error?.rejectionMessage,
    RejectionMessageCode: error?.rejectionMessageCode,
    OriginMessage: error?.originMessage,
    OutOfRangeLowLimit: error?.outOfRangeLowLimit,
    OutOfRangeHighLimit: error?.outOfRangeHighLimit,
    OutOfRangeCondition: error?.outOfRangeCondition,
  };
}
